import React from "react";
import { Outlet, Link, NavLink, Navigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Truck, CalendarDays, CalendarRange, MessageSquare, Mail, MapPin, Tag, Map, UserCircle2, LogOut, BadgePercent, ClipboardList, Wrench } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Toaster } from "sonner";

export default function AdminLayout() {
  const { user, ready, logout } = useAuth();
  const { pathname } = useLocation();

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="admin-loading">
        Loading admin…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/admin/login" replace state={{ from: pathname }} />;
  }
  if (user.role !== "admin") {
    return <Navigate to="/driver" replace />;
  }

  const nav = [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
    { to: "/admin/vehicles", label: "Vehicles", icon: Truck },
    { to: "/admin/drivers", label: "Drivers", icon: UserCircle2 },
    { to: "/admin/dispatch", label: "Dispatch", icon: ClipboardList },
    { to: "/admin/vehicle-issues", label: "Vehicle issues", icon: Wrench },
    { to: "/admin/tours", label: "Tours", icon: Map },
    { to: "/admin/bookings", label: "Bookings", icon: CalendarDays },
    { to: "/admin/calendar", label: "Calendar", icon: CalendarRange },
    { to: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
    { to: "/admin/transfer-locations", label: "Locations", icon: MapPin },
    { to: "/admin/transfer-route-prices", label: "Route prices", icon: Tag },
    { to: "/admin/transport-pricing", label: "Transport pricing", icon: BadgePercent },
    { to: "/admin/email-settings", label: "Email", icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex">
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-200 bg-white">
        <div className="px-5 py-5 border-b border-slate-200">
          <Link to="/admin" className="font-heading text-lg font-bold tracking-tight text-slate-900">
            IceFleet · Admin
          </Link>
          <p className="text-xs text-slate-500 mt-1">{user.email}</p>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            <LogOut size={16} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col">
        <div className="md:hidden flex items-center justify-between border-b border-slate-200 bg-white px-5 h-14">
          <Link to="/admin" className="font-heading font-bold">IceFleet · Admin</Link>
          <button
            onClick={logout}
            data-testid="admin-logout-mobile"
            className="text-sm text-slate-700"
          >
            Logout
          </button>
        </div>
        <div className="md:hidden flex overflow-x-auto border-b border-slate-200 bg-white hide-scrollbar">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `px-4 py-3 text-sm font-medium whitespace-nowrap ${
                  isActive ? "text-slate-900 border-b-2 border-slate-900" : "text-slate-500"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
        <main className="flex-1 p-5 sm:p-8">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
    </div>
  );
}
